/**
 * Standardized error display component for consistent error handling across the platform
 */

import React from 'react';
import { Button } from './Button';
import { InfoCircleIcon } from './icons/InfoCircleIcon';
import { WarningTriangleIcon } from './icons/WarningTriangleIcon';

export interface ErrorDisplayProps {
  title: string;
  message: string;
  type?: 'error' | 'warning' | 'not-found';
  showRetry?: boolean;
  onRetry?: () => void;
  isRetrying?: boolean;
  retryCount?: number;
  children?: React.ReactNode;
  className?: string;
}

const typeStyles = {
  error: {
    containerClass: 'bg-red-50 border-red-200',
    iconClass: 'bg-red-100 text-red-600',
    titleClass: 'text-red-800',
    messageClass: 'text-red-700',
    icon: (
      <InfoCircleIcon className="h-6 w-6" />
    ),
  },
  warning: {
    containerClass: 'bg-yellow-50 border-yellow-200',
    iconClass: 'bg-yellow-100 text-yellow-600',
    titleClass: 'text-yellow-800',
    messageClass: 'text-yellow-700',
    icon: (
      <WarningTriangleIcon className="h-6 w-6" />
    ),
  },
  'not-found': {
    containerClass: 'bg-gray-50 border-gray-200',
    iconClass: 'bg-gray-100 text-gray-600',
    titleClass: 'text-gray-800',
    messageClass: 'text-gray-700',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    ),
  },
};

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  title,
  message,
  type = 'error',
  showRetry = false,
  onRetry,
  isRetrying = false,
  retryCount = 0,
  children,
  className = '',
}) => {
  const styles = typeStyles[type];

  return (
    <div className={`border rounded-lg p-6 ${styles.containerClass} ${className}`}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${styles.iconClass}`}>
          {styles.icon}
        </div>
        <div className="ml-4 flex-1">
          <h3 className={`text-lg font-semibold mb-2 ${styles.titleClass}`}>
            {title}
          </h3>
          <p className={`text-sm mb-4 ${styles.messageClass}`}>
            {message}
            {retryCount > 0 && ` (Attempt ${retryCount + 1})`}
          </p>
          
          {showRetry && onRetry && (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={onRetry}
                loading={isRetrying}
                disabled={isRetrying}
              >
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </Button>
            </div>
          )}
          
          {children && (
            <div className="mt-4">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Convenience components for common error types
export const NetworkError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'> & { title?: string }> = ({
  title = 'Connection Problem',
  ...props
}) => (
  <ErrorDisplay
    type="warning"
    title={title}
    showRetry={true}
    {...props}
  />
);

export const NotFoundError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'> & { title?: string }> = ({
  title = 'Not Found',
  ...props
}) => (
  <ErrorDisplay
    type="not-found"
    title={title}
    {...props}
  />
);

export const ServerError: React.FC<Omit<ErrorDisplayProps, 'type' | 'title'> & { title?: string }> = ({
  title = 'Server Error',
  ...props
}) => (
  <ErrorDisplay
    type="error"
    title={title}
    showRetry={true}
    {...props}
  />
);